import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Row, Col, Button, Form, Modal, Card } from "react-bootstrap";
import {
  FaMoneyBillWave,
  FaEdit,
  FaTrash,
  FaEye,
  FaFileImage,
  FaPlus,
  FaFilter,
} from "react-icons/fa";
import { BsFillInfoCircleFill } from "react-icons/bs";
import "./expense-info.css";

const INFRA_CATEGORIES = [
  "Main Room",
  "Bathroom",
  "Solar",
  "Masjid",
  "Main Gate",
];

const LAB_CATEGORIES = [
  "Masonry Work",
  "Construction Work",
  "Electrical Work",
  "Sanitary Work",
  "Maintenance Work",
  "Painting Work",
  "Rent",
  "Utilities",
  "Salaries",
  "Office Supplies",
  "Marketing",
  "Travel",
  "Software",
  "Equipment",
  "Miscellaneous",
];

const ExpenseInfo = () => {
  const rowsPerPage = 10;
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);

  // TAB: "All" | "Infra" | "Lab"
  const [activeTab, setActiveTab] = useState("All");

  // Filters
  const [filterType, setFilterType] = useState("all");
  const [selectedDate, setSelectedDate] = useState("");
  const [selectedMonth, setSelectedMonth] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("All");

  // Modals
  const [showDialog, setShowDialog] = useState(false);
  const [selectedExpense, setSelectedExpense] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [expenseToDelete, setExpenseToDelete] = useState(null);

  // ───────── Fetch all expenses ─────────
  const fetchExpenses = async () => {
    setLoading(true);
    try {
      const res = await axios.get(
        "https://backend.ems.hiradentallab.com/api/expenses/all"
      );
      setExpenses(res.data);
    } catch (err) {
      console.error("Error fetching expenses:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExpenses();
  }, []);

  // ───────── Base filtered (date + category, WITHOUT type) ─────────
  const baseFiltered = useMemo(() => {
    let result = [...expenses];

    if (categoryFilter !== "All") {
      result = result.filter((e) => e.category === categoryFilter);
    }

    if (filterType === "daily" && selectedDate) {
      result = result.filter((e) => {
        const d = new Date(e.expenseDate).toISOString().split("T")[0];
        return d === selectedDate;
      });
    } else if (filterType === "monthly" && selectedMonth) {
      result = result.filter((e) => {
        const d = new Date(e.expenseDate);
        const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
          2,
          "0"
        )}`;
        return ym === selectedMonth;
      });
    } else if (filterType === "custom" && startDate && endDate) {
      const s = new Date(startDate);
      s.setHours(0, 0, 0, 0);
      const e = new Date(endDate);
      e.setHours(23, 59, 59, 999);
      result = result.filter((exp) => {
        const d = new Date(exp.expenseDate);
        return d >= s && d <= e;
      });
    }

    return result;
  }, [
    expenses,
    filterType,
    selectedDate,
    selectedMonth,
    startDate,
    endDate,
    categoryFilter,
  ]);

  // ───────── Apply activeTab on top of baseFiltered ─────────
  const filtered = useMemo(() => {
    if (activeTab === "All") return baseFiltered;
    return baseFiltered.filter((e) => (e.type || "Lab") === activeTab);
  }, [baseFiltered, activeTab]);

  // ───────── Subtotals per type ─────────
  const infraTotal = baseFiltered
    .filter((e) => (e.type || "Lab") === "Infra")
    .reduce((s, e) => s + (Number(e.amount) || 0), 0);

  const labTotal = baseFiltered
    .filter((e) => (e.type || "Lab") === "Lab")
    .reduce((s, e) => s + (Number(e.amount) || 0), 0);

  const combinedTotal = infraTotal + labTotal;

  const grandTotal = filtered.reduce(
    (sum, e) => sum + (Number(e.amount) || 0),
    0
  );
  const totalCount = filtered.length;

  // ───────── Daily / Monthly subtotals ─────────
  const dailyTotals = filtered.reduce((acc, e) => {
    const d = new Date(e.expenseDate).toISOString().split("T")[0];
    acc[d] = (acc[d] || 0) + (Number(e.amount) || 0);
    return acc;
  }, {});

  const monthlyTotals = filtered.reduce((acc, e) => {
    const d = new Date(e.expenseDate);
    const ym = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
    acc[ym] = (acc[ym] || 0) + (Number(e.amount) || 0);
    return acc;
  }, {});

  // ───────── Category options depend on active tab ─────────
  const availableCategories = useMemo(() => {
    const source =
      activeTab === "Infra"
        ? baseFiltered.filter((e) => (e.type || "Lab") === "Infra")
        : activeTab === "Lab"
        ? baseFiltered.filter((e) => (e.type || "Lab") === "Lab")
        : baseFiltered;

    return Array.from(new Set(source.map((e) => e.category).filter(Boolean)));
  }, [baseFiltered, activeTab]);

  // ───────── Month options ─────────
  const monthOptions = useMemo(() => {
    const set = new Set(
      expenses
        .map((e) => {
          const d = new Date(e.expenseDate);
          if (isNaN(d)) return null;
          return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
            2,
            "0"
          )}`;
        })
        .filter(Boolean)
    );

    return Array.from(set)
      .sort()
      .reverse()
      .map((value) => {
        const [y, m] = value.split("-");
        const label = new Date(Number(y), Number(m) - 1, 1).toLocaleString(
          "en-US",
          { month: "long", year: "numeric" }
        );
        return { value, label };
      });
  }, [expenses]);

  useEffect(() => {
    setCurrentPage(1);
  }, [
    activeTab,
    filterType,
    selectedDate,
    selectedMonth,
    startDate,
    endDate,
    categoryFilter,
  ]);

  useEffect(() => {
    if (
      categoryFilter !== "All" &&
      !availableCategories.includes(categoryFilter)
    ) {
      setCategoryFilter("All");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // ───────── Pagination ─────────
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));
  const pageRange = () => {
    const pages = [];
    let start = Math.max(currentPage - 1, 1);
    let end = Math.min(start + 2, totalPages);
    if (end - start < 2) start = Math.max(end - 2, 1);
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };
  const handlePageChange = (p) => {
    if (p >= 1 && p <= totalPages) setCurrentPage(p);
  };

  // ───────── View / Delete ─────────
  const handleShow = (exp) => {
    setSelectedExpense(exp);
    setShowDialog(true);
  };
  const handleClose = () => setShowDialog(false);

  const handleDeleteModal = (exp) => {
    setExpenseToDelete(exp);
    setShowDeleteModal(true);
  };
  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setExpenseToDelete(null);
  };
  const handleDeleteConfirmed = async () => {
    try {
      await axios.delete(
        `https://backend.ems.hiradentallab.com/api/expenses/delete/${expenseToDelete.expenseId}`
      );
      setExpenses((prev) =>
        prev.filter((e) => e.expenseId !== expenseToDelete.expenseId)
      );
      setShowDeleteModal(false);
      setExpenseToDelete(null);
    } catch (err) {
      console.error("Error deleting expense:", err);
    }
  };

  const clearFilters = () => {
    setFilterType("all");
    setSelectedDate("");
    setSelectedMonth("");
    setStartDate("");
    setEndDate("");
    setCategoryFilter("All");
  };

  const tabs = [
    { key: "All", label: "All", total: combinedTotal },
    { key: "Infra", label: "Infra", total: infraTotal },
    { key: "Lab", label: "Lab", total: labTotal },
  ];

  return (
    <div className="expense-page-wrap">
      {/* ============ HEADER ============ */}
      <h1 className="expense-page-title">
        <BsFillInfoCircleFill /> Expense Information
      </h1>
      <hr className="expense-divider" />

      <h1 className="expense-page-subtitle">
        <FaMoneyBillWave /> Expenses
      </h1>
      <hr className="expense-divider" />

      {/* ============ ADD BUTTON ============ */}
      <div className="mb-3">
        <Link to="/add-expense">
          <Button className="expense-add-btn">
            <FaPlus /> Add Expense
          </Button>
        </Link>
      </div>

      {/* ============ TABS ============ */}
      <div
        style={{
          display: "flex",
          gap: "8px",
          marginBottom: "20px",
          borderBottom: "2px solid #e6eefc",
          paddingBottom: "0px",
          flexWrap: "wrap",
        }}
      >
        {tabs.map((t) => {
          const isActive = activeTab === t.key;
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActiveTab(t.key)}
              style={{
                padding: "10px 24px",
                borderRadius: "6px 6px 0 0",
                border: "1px solid #004aad",
                borderBottom: isActive
                  ? "2px solid #ffffff"
                  : "1px solid #004aad",
                marginBottom: "-2px",
                background: isActive ? "#004aad" : "#ffffff",
                color: isActive ? "#ffffff" : "#004aad",
                fontWeight: 600,
                cursor: "pointer",
              }}
            >
              {t.label}
              <span
                style={{
                  marginLeft: "8px",
                  fontSize: "0.8rem",
                  opacity: 0.85,
                }}
              >
                (PKR {t.total.toLocaleString()})
              </span>
            </button>
          );
        })}
      </div>

      {/* ============ FILTERS ============ */}
      <Card className="expense-filter-card">
        <Card.Body>
          <div className="expense-filter-title">
            <FaFilter /> Filters
          </div>

          <Row className="g-3 align-items-end">
            <Col xs={12} sm={6} md={3} lg={2}>
              <Form.Label>Filter Type</Form.Label>
              <Form.Control
                as="select"
                value={filterType}
                onChange={(e) => setFilterType(e.target.value)}
              >
                <option value="all">All</option>
                <option value="daily">Daily</option>
                <option value="monthly">Monthly</option>
                <option value="custom">Custom Range</option>
              </Form.Control>
            </Col>

            {filterType === "daily" && (
              <Col xs={12} sm={6} md={3} lg={3}>
                <Form.Label>Select Date</Form.Label>
                <Form.Control
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                />
              </Col>
            )}

            {filterType === "monthly" && (
              <Col xs={12} sm={6} md={3} lg={3}>
                <Form.Label>Select Month</Form.Label>
                <Form.Control
                  as="select"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                >
                  <option value="">-- Select Month --</option>
                  {monthOptions.length === 0 ? (
                    <option value="" disabled>
                      No months available
                    </option>
                  ) : (
                    monthOptions.map(({ value, label }) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))
                  )}
                </Form.Control>
              </Col>
            )}

            {filterType === "custom" && (
              <>
                <Col xs={12} sm={6} md={3} lg={3}>
                  <Form.Label>Start Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                  />
                </Col>
                <Col xs={12} sm={6} md={3} lg={3}>
                  <Form.Label>End Date</Form.Label>
                  <Form.Control
                    type="date"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                  />
                </Col>
              </>
            )}

            <Col xs={12} sm={6} md={3} lg={3}>
              <Form.Label>Category</Form.Label>
              <Form.Control
                as="select"
                value={categoryFilter}
                onChange={(e) => setCategoryFilter(e.target.value)}
              >
                <option value="All">All Categories</option>
                {availableCategories.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </Form.Control>
            </Col>

            <Col xs={12} sm={6} md="auto" lg="auto">
              <Button className="expense-clear-btn" onClick={clearFilters}>
                Clear Filters
              </Button>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ============ SUMMARY CARDS ============ */}
      <Row className="expense-summary-row g-3">
        <Col xs={12} md={4}>
          <Card className="expense-grand-card">
            <Card.Body>
              <small>Grand Total (Filtered)</small>
              <h3>PKR {grandTotal.toLocaleString()}</h3>
              <span className="expense-entry-count">
                {totalCount} {totalCount === 1 ? "entry" : "entries"}
              </span>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={4}>
          <Card
            className="expense-grand-card"
            style={{ background: "#e6f0fb" }}
          >
            <Card.Body>
              <small>Infra Subtotal</small>
              <h3>PKR {infraTotal.toLocaleString()}</h3>
              <span className="expense-entry-count">
                {
                  baseFiltered.filter((e) => (e.type || "Lab") === "Infra")
                    .length
                }{" "}
                entries
              </span>
            </Card.Body>
          </Card>
        </Col>

        <Col xs={12} md={4}>
          <Card
            className="expense-grand-card"
            style={{ background: "#e6f7ef" }}
          >
            <Card.Body>
              <small>Lab Subtotal</small>
              <h3>PKR {labTotal.toLocaleString()}</h3>
              <span className="expense-entry-count">
                {
                  baseFiltered.filter((e) => (e.type || "Lab") === "Lab")
                    .length
                }{" "}
                entries
              </span>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* ============ COMBINED SUBTOTALS (Daily + Monthly) ============ */}
      {(filterType === "monthly" || filterType === "all") && (
        <Card className="expense-subtotal-card" style={{ marginTop: "16px" }}>
          <Card.Body>
            <Row className="g-4">
              {/* ---- Daily ---- */}
              <Col xs={12} md={filterType === "all" ? 6 : 12}>
                <div
                  style={{
                    borderRight:
                      filterType === "all" ? "1px solid #e6eefc" : "none",
                    paddingRight: filterType === "all" ? "12px" : 0,
                    height: "100%",
                  }}
                  className="subtotal-col"
                >
                  <span className="expense-category-title">
                    Daily Subtotals
                  </span>
                  <div
                    className="expense-chip-wrap"
                    style={{ marginTop: "8px" }}
                  >
                    {Object.keys(dailyTotals).length === 0 ? (
                      <span className="expense-empty-text">No data</span>
                    ) : (
                      Object.entries(dailyTotals)
                        .sort((a, b) => (a[0] < b[0] ? 1 : -1))
                        .slice(0, 15)
                        .map(([date, total]) => (
                          <span key={date} className="expense-sub-chip">
                            <strong>{date}:</strong> PKR{" "}
                            {total.toLocaleString()}
                          </span>
                        ))
                    )}
                  </div>
                </div>
              </Col>

              {/* ---- Monthly ---- */}
              {filterType === "all" && (
                <Col xs={12} md={6}>
                  <div className="subtotal-col">
                    <span className="expense-category-title">
                      Monthly Subtotals
                    </span>
                    <div
                      className="expense-chip-wrap"
                      style={{ marginTop: "8px" }}
                    >
                      {Object.keys(monthlyTotals).length === 0 ? (
                        <span className="expense-empty-text">No data</span>
                      ) : (
                        Object.entries(monthlyTotals)
                          .sort((a, b) => (a[0] < b[0] ? 1 : -1))
                          .map(([ym, total]) => (
                            <span key={ym} className="expense-sub-chip">
                              <strong>{ym}:</strong> PKR{" "}
                              {total.toLocaleString()}
                            </span>
                          ))
                      )}
                    </div>
                  </div>
                </Col>
              )}
            </Row>
          </Card.Body>
        </Card>
      )}

      {/* ============ TABLE ============ */}
      <div className="expense-table-wrap">
        {loading ? (
          <p className="p-4 text-center mb-0">Loading...</p>
        ) : (
          <>
            <div className="table-responsive">
              <table className="expense-table">
                <thead className="expense-thead">
                  <tr>
                    <th>SR</th>
                    <th>ID</th>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Category</th>
                    <th>Description</th>
                    <th>Paid To</th>
                    <th>Method</th>
                    <th>Bill</th>
                    <th>Amount (PKR)</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.length === 0 ? (
                    <tr>
                      <td colSpan="11" className="expense-no-data">
                        No expenses available
                      </td>
                    </tr>
                  ) : (
                    filtered
                      .slice(
                        (currentPage - 1) * rowsPerPage,
                        currentPage * rowsPerPage
                      )
                      .map((exp, index) => (
                        <tr key={exp._id}>
                          <td>
                            {(currentPage - 1) * rowsPerPage + index + 1}
                          </td>
                          <td>{exp.expenseId}</td>
                          <td>
                            <span
                              style={{
                                padding: "2px 10px",
                                borderRadius: "12px",
                                fontSize: "0.75rem",
                                fontWeight: 600,
                                background:
                                  (exp.type || "Lab") === "Infra"
                                    ? "#dbeafe"
                                    : "#dcfce7",
                                color:
                                  (exp.type || "Lab") === "Infra"
                                    ? "#1e40af"
                                    : "#166534",
                              }}
                            >
                              {exp.type || "Lab"}
                            </span>
                          </td>
                          <td>
                            {new Date(exp.expenseDate).toLocaleDateString()}
                          </td>
                          <td>{exp.category}</td>
                          <td>{exp.description}</td>
                          <td>{exp.paidTo || "—"}</td>
                          <td>{exp.paymentMethod}</td>
                          <td>
                            {exp.billImage ? (
                              <a
                                href={`https://backend.ems.hiradentallab.com/${exp.billImage}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="expense-bill-link"
                              >
                                <FaFileImage />
                              </a>
                            ) : (
                              <span className="text-muted">—</span>
                            )}
                          </td>
                          <td className="expense-amount-cell">
                            {Number(exp.amount).toLocaleString()}
                          </td>
                          <td>
                            <div className="expense-actions-cell">
                              <Button
                                className="expense-action-btn view"
                                onClick={() => handleShow(exp)}
                                title="View"
                              >
                                <FaEye />
                              </Button>
                              <Link to={`/edit-expense/${exp.expenseId}`}>
                                <Button
                                  className="expense-action-btn edit"
                                  title="Edit"
                                >
                                  <FaEdit />
                                </Button>
                              </Link>
                              <Button
                                className="expense-action-btn delete"
                                onClick={() => handleDeleteModal(exp)}
                                title="Delete"
                              >
                                <FaTrash />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      ))
                  )}
                </tbody>
                <tfoot className="expense-tfoot">
                  <tr>
                    <td colSpan="9" className="expense-tfoot-label">
                      Subtotal of Visible Filter ({activeTab}):
                    </td>
                    <td className="expense-tfoot-total">
                      PKR {grandTotal.toLocaleString()}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>

            {/* ---------- Pagination ---------- */}
            <div className="expense-pagination-row">
              <p className="expense-showing-text">
                Showing{" "}
                {filtered.length === 0
                  ? 0
                  : (currentPage - 1) * rowsPerPage + 1}{" "}
                to {Math.min(currentPage * rowsPerPage, filtered.length)} of{" "}
                {filtered.length} entries
              </p>

              <ul className="expense-pagination">
                <li
                  className={`page-item ${
                    currentPage === 1 ? "disabled" : ""
                  }`}
                >
                  <span
                    className="page-link"
                    onClick={() => handlePageChange(currentPage - 1)}
                  >
                    Previous
                  </span>
                </li>
                {pageRange().map((page) => (
                  <li
                    key={page}
                    className={`page-item ${
                      currentPage === page ? "active" : ""
                    }`}
                  >
                    <span
                      className="page-link"
                      onClick={() => handlePageChange(page)}
                    >
                      {page}
                    </span>
                  </li>
                ))}
                <li
                  className={`page-item ${
                    currentPage === totalPages ? "disabled" : ""
                  }`}
                >
                  <span
                    className="page-link"
                    onClick={() => handlePageChange(currentPage + 1)}
                  >
                    Next
                  </span>
                </li>
              </ul>
            </div>
          </>
        )}
      </div>

      {/* ============ VIEW MODAL ============ */}
      {selectedExpense && (
        <Modal
          show={showDialog}
          onHide={handleClose}
          className="custom-modal"
          centered
        >
          <Modal.Header
            style={{ backgroundColor: "#004aad", color: "#ffffff" }}
          >
            <Modal.Title style={{ color: "#ffffff" }}>
              Expense Details
            </Modal.Title>
            <button
              onClick={handleClose}
              style={{
                color: "white",
                background: "transparent",
                border: "none",
                fontSize: "40px",
                lineHeight: "1",
                marginLeft: "auto",
              }}
            >
              ×
            </button>
          </Modal.Header>
          <Modal.Body>
            <div className="expense-info-block">
              <p>
                <strong>Expense ID:</strong> {selectedExpense.expenseId}
              </p>
              <p>
                <strong>Type:</strong> {selectedExpense.type || "Lab"}
              </p>
              <p>
                <strong>Date:</strong>{" "}
                {new Date(selectedExpense.expenseDate).toLocaleDateString()}
              </p>
              <p>
                <strong>Category:</strong> {selectedExpense.category}
              </p>
              <p>
                <strong>Description:</strong> {selectedExpense.description}
              </p>
              <p>
                <strong>Amount:</strong> PKR{" "}
                {Number(selectedExpense.amount).toLocaleString()}
              </p>
              <p>
                <strong>Paid To:</strong> {selectedExpense.paidTo || "N/A"}
              </p>
              <p>
                <strong>Payment Method:</strong>{" "}
                {selectedExpense.paymentMethod}
              </p>
              <p>
                <strong>Notes:</strong> {selectedExpense.notes || "N/A"}
              </p>
              <p>
                <strong>Added By:</strong>{" "}
                {selectedExpense.addedByName || "N/A"}
              </p>
              <p>
                <strong>Status:</strong> {selectedExpense.status}
              </p>

              {selectedExpense.billImage && (
                <div className="mt-3">
                  <strong>Bill Image:</strong>
                  <div>
                    {selectedExpense.billImage
                      .toLowerCase()
                      .endsWith(".pdf") ? (
                      <a
                        href={`https://backend.ems.hiradentallab.com/${selectedExpense.billImage}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="expense-bill-link-view"
                      >
                        📄 View Bill PDF
                      </a>
                    ) : (
                      <img
                        src={`https://backend.ems.hiradentallab.com/${selectedExpense.billImage}`}
                        alt="Bill"
                        className="expense-bill-preview"
                      />
                    )}
                  </div>
                </div>
              )}
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={handleClose}>
              Close
            </Button>
          </Modal.Footer>
        </Modal>
      )}

      {/* ============ DELETE MODAL ============ */}
      {expenseToDelete && (
        <Modal show={showDeleteModal} onHide={closeDeleteModal} centered>
          <Modal.Header closeButton>
            <Modal.Title>Confirm Delete</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            Are you sure you want to delete expense{" "}
            <strong>#{expenseToDelete.expenseId}</strong> (
            {expenseToDelete.description})?
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={closeDeleteModal}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleDeleteConfirmed}>
              Delete
            </Button>
          </Modal.Footer>
        </Modal>
      )}
    </div>
  );
};

export default ExpenseInfo;